import { Grid, Stack } from "@mui/material";
import { ComponentType, CSSProperties, useEffect } from "react";
import {
  CustomField,
  CustomFieldContext,
  CustomFieldType,
  GetCustomFieldDefinitionsQuery,
} from "../../../graphql/generated";
import SelectCustomFieldInput from "./custom-field-inputs/SelectCustomFieldInput";
import StringCustomFieldInput from "./custom-field-inputs/StringCustomFieldInput";
import NumberCustomFieldInput from "./custom-field-inputs/NumberCustomFieldInput";
import MultiSelectCustomFieldInput from "./custom-field-inputs/MultiSelectCustomFieldInput";
import DateInput from "./custom-field-inputs/DateInput";
import TimeInput from "./custom-field-inputs/TimeInput";
import BooleanCustomFieldInput from "./custom-field-inputs/BooleanCustomFieldInput";
import { castArray } from "lodash";
import DateTimeInput from "./custom-field-inputs/DateTimeInput";

type CustomFieldDefinitionFormData =
  GetCustomFieldDefinitionsQuery["cutomFieldDefinitions"]["data"][0];

/** Coerce defaultOption string to the value type expected by the field type */
function defaultOptionToValue(
  defaultOption: string,
  type: CustomFieldType
): CustomFieldValue {
  switch (type) {
    case CustomFieldType.Multiselect:
      return [defaultOption];
    case CustomFieldType.Number: {
      const n = Number(defaultOption);
      return Number.isNaN(n) ? defaultOption : n;
    }
    case CustomFieldType.Boolean:
      return defaultOption === "true";
    case CustomFieldType.Select:
    case CustomFieldType.String:
    case CustomFieldType.Date:
    case CustomFieldType.Datetime:
    case CustomFieldType.Time:
    default:
      return defaultOption;
  }
}

function getEffectiveValue(
  customField: CustomField | undefined,
  definition: CustomFieldDefinitionFormData
): CustomFieldValue | null {
  if (customField?.value !== undefined && customField?.value !== null) {
    return customField.value as CustomFieldValue;
  }
  const defaultOption = definition.defaultOption;
  if (!defaultOption) return null;
  return defaultOptionToValue(defaultOption, definition.type);
}

export type CustomFieldsFormProps = {
  customFieldDefinitions: CustomFieldDefinitionFormData[];
  customFields: CustomField[] | null;
  onChange: (customFields: CustomField[]) => void;
  context: CustomFieldContext;
  disabled?: boolean;
};

export type CustomFieldValue = string | number | boolean | string[] | number[];

export type CustomFieldInputProps = {
  value: CustomFieldValue | null;
  onChange: (value: CustomFieldValue | null) => void;
  customFieldDefinition: CustomFieldDefinitionFormData;
  label?: string;
  style?: CSSProperties;
  disabled?: boolean;
};

export type CustomFieldInputComponent = ComponentType<CustomFieldInputProps>;

const CustomFieldInputComponentByTarget: {
  [key in CustomFieldType]: CustomFieldInputComponent | null;
} = {
  [CustomFieldType.Select]: SelectCustomFieldInput,
  [CustomFieldType.Boolean]: BooleanCustomFieldInput,
  [CustomFieldType.Date]: DateInput,
  [CustomFieldType.Datetime]: DateTimeInput,
  [CustomFieldType.Multiselect]: MultiSelectCustomFieldInput,
  [CustomFieldType.Number]: NumberCustomFieldInput,
  [CustomFieldType.String]: StringCustomFieldInput,
  [CustomFieldType.Time]: TimeInput,
};

const requirementCustomFieldRangeContexts = [
  CustomFieldContext.Shipment,
  CustomFieldContext.Customer,
  CustomFieldContext.Location,
  CustomFieldContext.Commodity,
];

const CustomFieldsForm = ({
  customFields,
  customFieldDefinitions,
  onChange,
  context,
  disabled,
}: CustomFieldsFormProps) => {
  // Propagate default values to parent state so fields with defaultOption are included on submit
  useEffect(() => {
    if (disabled) return;
    const hasValue = (key: string) =>
      customFields?.some((cf) => cf.key === key) ?? false;
    const needsDefault = customFieldDefinitions.filter(
      (def) => def.defaultOption != null && !hasValue(def.key)
    );
    if (needsDefault.length === 0) return;
    const base = customFields ?? [];
    const withDefaults = [
      ...base,
      ...needsDefault.map((def) => ({
        key: def.key,
        value: defaultOptionToValue(def.defaultOption!, def.type),
      })),
    ];
    onChange(withDefaults);
  }, [customFieldDefinitions, customFields, onChange, disabled]);

  return (
    <Stack direction="column" spacing={2}>
      {customFieldDefinitions.map((customFieldDefinition, index) => {
        const InputComponent =
          CustomFieldInputComponentByTarget[customFieldDefinition.type];
        if (!InputComponent) {
          return null;
        }
        const customField = customFields?.find(
          (customField) => customField.key === customFieldDefinition.key
        );
        if (
          customFieldDefinition.isRequirement &&
          customFieldDefinition.requirementOptions?.isRange &&
          requirementCustomFieldRangeContexts.includes(context)
        ) {
          const customFieldValue = castArray(customField?.value);
          return (
            <Stack key={customFieldDefinition._id} direction="row">
              <InputComponent
                customFieldDefinition={customFieldDefinition}
                value={customFieldValue[0] || null}
                disabled={disabled}
                onChange={(value) => {
                  onChange(
                    (
                      customFields?.filter(
                        (customField) =>
                          customField.key !== customFieldDefinition.key
                      ) || []
                    ).concat({
                      key: customFieldDefinition.key,
                      value: [value, customFieldValue[1]],
                    })
                  );
                }}
                label={customFieldDefinition.label + " (min)"}
              />
              <InputComponent
                customFieldDefinition={customFieldDefinition}
                value={customFieldValue[1] || null}
                disabled={disabled}
                onChange={(value) => {
                  onChange(
                    (
                      customFields?.filter(
                        (customField) =>
                          customField.key !== customFieldDefinition.key
                      ) || []
                    ).concat({
                      key: customFieldDefinition.key,
                      value: [customFieldValue[0], value],
                    })
                  );
                }}
                label={customFieldDefinition.label + " (max)"}
              />
            </Stack>
          );
        }
        return (
          <Grid
            style={{
              marginTop: index > 0 ? 20 : 0,
              marginBottom: 16,
            }}
          >
            <InputComponent
              style={{ marginTop: 5 }}
              key={customFieldDefinition._id}
              customFieldDefinition={customFieldDefinition}
              disabled={disabled}
              value={
                getEffectiveValue(customField, customFieldDefinition) ?? null
              }
              onChange={(value) => {
                if (value === null || value === undefined) {
                  onChange(
                    customFields?.filter(
                      (customField) =>
                        customField.key !== customFieldDefinition.key
                    ) || []
                  );
                  return;
                }
                onChange(
                  (
                    customFields?.filter(
                      (customField) =>
                        customField.key !== customFieldDefinition.key
                    ) || []
                  ).concat({
                    key: customFieldDefinition.key,
                    value,
                  })
                );
              }}
            />
          </Grid>
        );
      })}
    </Stack>
  );
};

export default CustomFieldsForm;
